import { useEffect, useState } from "react";
import { MessageSquare, Pencil } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, btnPrimary, btnGhost, inputCls } from "./shared";

const VALID = /^[A-Za-z][A-Za-z0-9]{2,10}$/;

function SenderForm({ initial, onSaved, onCancel }) {
  const [value, setValue] = useState(initial);
  const [busy, setBusy] = useState(false);
  const ok = VALID.test(value.trim());

  const save = async () => {
    setBusy(true);
    try {
      const res = await api.put("/admin/sms-settings", { avsender: value.trim() });
      toast.success(`SMS sendes nå fra «${res.data.avsender}»`);
      onSaved(res.data.avsender);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
      <div className="flex-1">
        <input value={value} onChange={(e) => setValue(e.target.value)} maxLength={11} placeholder="F.eks. Bordverket" className={inputCls} data-testid="sender-input" autoFocus />
        <p className={`mt-1.5 text-xs ${value && !ok ? "text-red-400" : "text-[#A8A29E]/80"}`} data-testid="sender-hint">
          3–11 tegn, bare bokstaver A–Z og tall, må starte med en bokstav. Ingen mellomrom eller æøå. ({value.length}/11)
        </p>
      </div>
      <div className="flex gap-2">
        {onCancel && <button onClick={onCancel} className={btnGhost} data-testid="sender-cancel-btn">Avbryt</button>}
        <button onClick={save} disabled={!ok || busy} className={btnPrimary} data-testid="sender-save-btn">{busy ? "Lagrer…" : "Lagre"}</button>
      </div>
    </div>
  );
}

export function SenderSettings() {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    api.get("/admin/sms-settings").then((res) => setData(res.data)).catch(() => {});
  }, []);

  if (!data) return null;
  const saved = (avsender) => {
    setData({ avsender, lagret: true });
    setEditing(false);
  };
  return (
    <Card className="mt-6" title="SMS-avsendernavn" icon={MessageSquare} testid="sender-settings">
      {data.lagret && !editing ? (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-[#A8A29E]">
            Alle SMS-er sendes fra <span className="font-semibold text-[#E5C564]" data-testid="sender-current">{data.avsender}</span>
          </p>
          <button onClick={() => setEditing(true)} className={btnGhost} data-testid="sender-edit-btn">
            <Pencil className="h-4 w-4" /> Endre
          </button>
        </div>
      ) : (
        <>
          <p className="mb-4 text-sm text-[#A8A29E]">
            {data.lagret
              ? "Skriv inn det nye avsendernavnet."
              : `Skriv inn navnet gjestene skal se som avsender på alle SMS-er. Du trenger bare gjøre dette én gang. Til du lagrer, brukes «${data.avsender}».`}
          </p>
          <SenderForm initial={data.lagret ? data.avsender : ""} onSaved={saved} onCancel={data.lagret ? () => setEditing(false) : null} />
        </>
      )}
    </Card>
  );
}
